import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import { MARKETING_SERVICES, IT_SERVICES } from "../data/mock";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function ServiceDetail({ category }) {
  const { slug } = useParams();
  const list = category === "it" ? IT_SERVICES : MARKETING_SERVICES;
  const base = category === "it" ? "/it-services" : "/services";
  const label = category === "it" ? "IT Services" : "Services";
  const labelLower = category === "it" ? "IT Services" : "services";
  const idx = list.findIndex((s) => s.slug === slug);
  if (idx === -1) return <Navigate to={base} replace />;
  const s = list[idx];
  const others = list.filter((_, i) => i !== idx).slice(0, 3);

  return (
    <div data-testid="service-detail-page" className="pt-16" key={slug}>
      {/* Hero */}
      <section className="bg-[#FDFBF7] pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <Link to={base} data-testid="service-detail-back-link"
              className="inline-flex items-center gap-1.5 text-sm text-[#5C4B51] hover:text-[#6C0B1B] transition-colors duration-200 mb-8">
              <ArrowLeft size={15} /> All {labelLower}
            </Link>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#6C0B1B] text-white flex items-center justify-center">
                    <s.icon size={22} />
                  </div>
                  <span className="text-xs text-[#A31621] font-semibold uppercase tracking-[0.2em]">{label}</span>
                </div>
                <h1 data-testid="service-detail-title" className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1014]">
                  {s.title}
                </h1>
                <p className="mt-4 text-lg font-medium text-[#6C0B1B] italic">{s.tagline}</p>
                <p className="mt-5 text-lg text-[#5C4B51] leading-relaxed">{s.description}</p>
                <Link to="/contact" data-testid="service-detail-hero-cta"
                  className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#6C0B1B] text-white px-7 py-3.5 font-medium hover:bg-[#8C1126] transition-colors duration-200">
                  Get a Free Consultation <ArrowUpRight size={17} />
                </Link>
              </div>
              <motion.div {...fadeUp} className="relative">
                <img src={s.heroImage} alt={s.title}
                  className="w-full h-[300px] sm:h-[420px] object-cover rounded-2xl shadow-[0_30px_60px_-20px_rgba(108,11,27,0.28)]" />
                {s.result && (
                  <div className="absolute -bottom-5 left-5 rounded-xl bg-white border border-[#6C0B1B]/10 px-5 py-3.5 shadow-[0_20px_40px_-15px_rgba(108,11,27,0.2)] flex items-center gap-2.5">
                    <Sparkles size={16} className="text-[#D4A24C]" />
                    <span className="text-sm font-semibold text-[#1A1014]">{s.result}</span>
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview + What's included */}
      <section className="bg-[#F3EFE9] py-20 mt-6">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <motion.div {...fadeUp}>
              <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">Overview</p>
              <p className="text-lg text-[#1A1014] leading-relaxed">{s.overview}</p>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div {...fadeUp} data-testid="service-detail-included"
              className="bg-white border border-[#6C0B1B]/10 rounded-2xl p-8 shadow-[0_20px_40px_-15px_rgba(108,11,27,0.1)]">
              <h3 className="font-heading text-2xl font-semibold text-[#1A1014]">What's included</h3>
              <ul className="mt-5 space-y-3.5">
                {s.included.map((item) => (
                  <li key={item} className="flex gap-3 items-start">
                    <CheckCircle2 size={19} className="text-[#6C0B1B] shrink-0 mt-0.5" />
                    <span className="text-[15px] text-[#1A1014] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="bg-[#FDFBF7] py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="mb-10">
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">Explore more</p>
            <h2 className="font-heading text-4xl font-medium tracking-tight text-[#1A1014]">Related {labelLower}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((o, i) => (
              <motion.div key={o.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <Link to={`${base}/${o.slug}`} data-testid={`service-detail-related-${o.slug}`}
                  className="group block bg-white border border-[#6C0B1B]/10 rounded-2xl overflow-hidden hover:border-[#6C0B1B]/30 transition-colors duration-300 shadow-[0_20px_40px_-15px_rgba(108,11,27,0.08)]">
                  <div className="h-40 overflow-hidden">
                    <img src={o.heroImage} alt={o.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2.5">
                      <o.icon size={18} className="text-[#6C0B1B]" />
                      <h3 className="font-heading text-xl font-semibold text-[#1A1014]">{o.title}</h3>
                    </div>
                    <p className="mt-2 text-sm text-[#5C4B51] leading-relaxed">{o.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#A31621]">
                      Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFBF7] pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="rounded-2xl bg-[#1A1014] text-white p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light">Ready to get started with {s.title}?</h2>
              <p className="mt-2 text-white/60">Tell us your goal — we'll map the fastest route to it.</p>
            </div>
            <Link to="/contact" data-testid="service-detail-contact-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#6C0B1B] text-white px-7 py-3.5 font-medium hover:bg-[#8C1126] transition-colors duration-200 shrink-0">
              Start a Conversation <ArrowUpRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
